<script setup lang="ts">
import ActionStatusBtn from "~/components/Pages/training-plan/project/steps/project-type/ActionStatusBtn.vue";
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import DisabledTooltipWrapper from "~/components/Common/DisabledTooltipWrapper.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import {
  SESSION_NOTIFICATION_DELIVERY_ICON,
  SessionNotificationDeliveryEnum,
} from "~/utils/enums/session-notifications.enums";

const props = withDefaults(
  defineProps<{
    /** Provider addresses resolved from the session; empty disables the provider branch (AC-ENVOI.13). */
    providerRecipients?: string[];
    /** Whether a step of the session names a trainer; false disables the trainers branch. */
    hasTrainers?: boolean;
    /** Off where the send targets a selection of attendees: only participants and managers are offered. */
    showSessionRecipients?: boolean;
  }>(),
  { providerRecipients: () => [], hasTrainers: false, showSessionRecipients: true },
);

const emit = defineEmits<{
  (e: "hide"): void;
  (e: "choose", choice: SessionNotificationDeliveryEnum): void;
}>();

const hasProviderContact = computed(() => props.providerRecipients.length > 0);
</script>

<template>
  <CenterModal class="flex flex-col w-full max-w-[720px] h-[688px]" @hide="emit('hide')">
    <template #body>
      <div class="flex items-center justify-end h-12 px-5">
        <SvgIcon class="w-6 h-6 text-secondary-color cursor-pointer" name="close" @click="emit('hide')"></SvgIcon>
      </div>
      <div class="flex flex-col gap-4 px-16 pt-6">
        <h3 class="heading_3_demi leading-6 p-2">A qui souhaitez-vous envoyer une notification ?</h3>

        <div class="flex flex-col gap-5 w-full max-w-[560px]">
          <ActionStatusBtn @click="emit('choose', SessionNotificationDeliveryEnum.PARTICIPANTS)">
            <template #title>
              <span class="flex flex-col gap-1 text-primary-color">
                <SvgIcon
                  :name="SESSION_NOTIFICATION_DELIVERY_ICON[SessionNotificationDeliveryEnum.PARTICIPANTS]"
                  class="w-6 h-6"
                ></SvgIcon>
                <span>Participants</span>
              </span>
            </template>
            <template #subTitle>
              <span class="block mt-1 meta_regular"> Vous pourrez ajouter des destinataires en copie (managers…) </span>
            </template>
          </ActionStatusBtn>

          <ActionStatusBtn @click="emit('choose', SessionNotificationDeliveryEnum.MANAGERS)">
            <template #title>
              <span class="flex flex-col gap-1 text-primary-color">
                <SvgIcon
                  :name="SESSION_NOTIFICATION_DELIVERY_ICON[SessionNotificationDeliveryEnum.MANAGERS]"
                  class="w-6 h-6"
                ></SvgIcon>
                <span>Managers</span>
              </span>
            </template>
          </ActionStatusBtn>

          <!-- Disabled cards fade as a whole: the label keeps its own color over the button's grey. -->
          <DisabledTooltipWrapper v-if="showSessionRecipients" :disabled="!hasTrainers" custom-tooltip>
            <template #button>
              <ActionStatusBtn
                class="disabled:opacity-40"
                :disabled="!hasTrainers"
                @click="hasTrainers && emit('choose', SessionNotificationDeliveryEnum.TRAINERS)"
              >
                <template #title>
                  <span class="flex flex-col gap-1 text-primary-color">
                    <SvgIcon
                      :name="SESSION_NOTIFICATION_DELIVERY_ICON[SessionNotificationDeliveryEnum.TRAINERS]"
                      class="w-6 h-6"
                    ></SvgIcon>
                    <span>Formateurs</span>
                  </span>
                </template>
              </ActionStatusBtn>
            </template>
            <template #tooltip-content>
              <span class="meta_regular whitespace-nowrap">Aucun formateur renseigné sur les étapes</span>
            </template>
          </DisabledTooltipWrapper>

          <DisabledTooltipWrapper v-if="showSessionRecipients" :disabled="!hasProviderContact" custom-tooltip>
            <template #button>
              <ActionStatusBtn
                class="disabled:opacity-40"
                :disabled="!hasProviderContact"
                @click="hasProviderContact && emit('choose', SessionNotificationDeliveryEnum.TRAINING_PARTNER)"
              >
                <template #title>
                  <span class="flex flex-col gap-1 text-primary-color">
                    <SvgIcon
                      :name="SESSION_NOTIFICATION_DELIVERY_ICON[SessionNotificationDeliveryEnum.TRAINING_PARTNER]"
                      class="w-6 h-6"
                    ></SvgIcon>
                    <span>Prestataire</span>
                  </span>
                </template>
              </ActionStatusBtn>
            </template>
            <template #tooltip-content>
              <span class="meta_regular whitespace-nowrap">
                Aucun contact prestataire n'est renseigné sur cette session.
              </span>
            </template>
          </DisabledTooltipWrapper>
        </div>
      </div>
    </template>
  </CenterModal>
</template>
